import { Capacitor } from '@capacitor/core'
import { Check, Loader2, X } from 'lucide-react'
import { useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { Link } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { useVoxnela } from '../../contexts/VoxnelaContext'
import { useBillingCatalog } from '../../hooks/useBillingCatalog'
import { createStripeCheckoutSession } from '../../lib/api'
import {
  billingOffer,
  type BillingPeriod,
  type PaidMembershipPlan,
} from '../../lib/billingCatalog'
import {
  purchaseNativeMembershipPlan,
  restoreNativeMembershipProfile,
  isCurrentSubscription,
  isPlanUpgradeAvailable,
  membershipPeriodLabel,
} from '../../lib/membership'
import type { PaywallReason } from '../../lib/paywall'
import { VoxnelaStudioHeroArtwork } from './VoxnelaStudioHeroArtwork'
import { haptic } from '../../lib/native'
import { useUiText } from '../../i18n/ui'
import { formatPaywallMessage, paywallMessages } from '../../i18n/paywall'
import { guestMessages } from '../../i18n/guest'

interface PaywallSheetProps {
  open: boolean
  onClose: () => void
  initialPlan?: PaidMembershipPlan
  reason?: PaywallReason
  message?: string
  onOpenTerms?: () => void
  onOpenPrivacy?: () => void
}

export function PaywallSheet(props: PaywallSheetProps) {
  return props.open ? <PaywallSheetContent key={props.initialPlan ?? 'default'} {...props} /> : null
}

function PaywallSheetContent({ onClose, initialPlan, message, onOpenTerms, onOpenPrivacy }: PaywallSheetProps) {
  const { locale, text } = useUiText()
  const copy = paywallMessages[locale]
  const { user, ensureGuestSession } = useAuth()
  const { membership, refreshWorkspace, membershipLoading, membershipSyncPending, workspaceError } = useVoxnela()
  const { catalog, loading: catalogLoading } = useBillingCatalog()

  // Default selected tier
  const initialTier: PaidMembershipPlan =
    initialPlan === 'studio' || (!initialPlan && user && membership.plan !== 'free')
      ? 'studio'
      : 'pro'

  const [selectedTier, setSelectedTier] = useState<PaidMembershipPlan>(initialTier)
  const [selectedPeriod, setSelectedPeriod] = useState<BillingPeriod>('monthly')
  const [working, setWorking] = useState(false)
  const [error, setError] = useState('')
  const [restoring, setRestoring] = useState(false)
  const [notice, setNotice] = useState('')

  const monthlyOffer = billingOffer(catalog, selectedTier, 'monthly')
  const yearlyOffer = billingOffer(catalog, selectedTier, 'yearly')
  const currentOffer = selectedPeriod === 'yearly' ? yearlyOffer : monthlyOffer

  // Check upgrade/current status
  const isSelectedCurrent = isCurrentSubscription(
    membership.plan,
    membership.billingPeriod,
    selectedTier,
    selectedPeriod,
  )

  const isSelectedUpgradeAvailable = isPlanUpgradeAvailable(
    membership.plan,
    membership.billingPeriod,
    selectedTier,
    selectedPeriod,
  )

  const isActionDisabled =
    isSelectedCurrent || !isSelectedUpgradeAvailable || working || restoring || catalogLoading || membershipLoading

  const ctaLabel = useMemo(() => {
    if (working) return null
    if (isSelectedCurrent) {
      const planName = selectedTier === 'studio' ? 'Studio' : 'Pro'
      const periodName = membershipPeriodLabel(selectedPeriod, locale) ?? ''
      return formatPaywallMessage(copy.alreadyMember, { plan: planName, period: periodName })
    }
    if (!isSelectedUpgradeAvailable) return copy.currentMembership
    return copy.startFreeTrial
  }, [
    isSelectedCurrent,
    isSelectedUpgradeAvailable,
    membership.billingPeriod,
    membership.plan,
    selectedPeriod,
    selectedTier,
    working,
    copy,
    locale,
  ])

  // Pro and Studio are the two public App Store tiers.
  const benefits = useMemo(() => {
    const generationBenefit = selectedTier === 'studio' ? copy.studioGeneration : copy.proGeneration
    return copy.benefits.map((benefit) => ({
      ...benefit,
      lead: formatPaywallMessage(benefit.lead, { generationBenefit }),
    }))
  }, [copy, selectedTier])

  const startSubscription = async () => {
    // In the app a purchase never needs an account; it belongs to the guest
    // session until the person chooses to sign in.
    const buyer = user ?? await ensureGuestSession()
    if (!buyer) {
      setError(Capacitor.isNativePlatform() ? guestMessages[locale].guestSessionFailed : copy.signInRequired)
      return
    }
    setWorking(true)
    setError('')
    void haptic('selection')

    try {
      if (Capacitor.isNativePlatform()) {
        await purchaseNativeMembershipPlan({
          userId: buyer.uid,
          plan: selectedTier,
          billingPeriod: selectedPeriod,
        })
        await refreshWorkspace()
        return
      }

      if (currentOffer.source === 'stripe' && currentOffer.productId) {
        const session = await createStripeCheckoutSession(currentOffer.productId)
        window.location.assign(session.url)
        return
      }

      throw new Error(
        copy.catalogUnavailable,
      )
    } catch (cause) {
      setError(
        cause instanceof Error
          ? cause.message
          : copy.paymentUnavailable,
      )
    } finally {
      setWorking(false)
    }
  }

  const restorePurchases = async () => {
    const owner = user ?? await ensureGuestSession()
    if (!owner) {
      setError(Capacitor.isNativePlatform() ? guestMessages[locale].guestSessionFailed : copy.signInRequired)
      return
    }
    setRestoring(true)
    setError('')
    setNotice('')
    try {
      const restored = await restoreNativeMembershipProfile(owner.uid, membership)
      if (!restored) { setError(copy.restoreEmpty); return }
      await refreshWorkspace()
      setNotice(copy.restoreSuccess)
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : copy.paymentUnavailable)
    } finally {
      setRestoring(false)
    }
  }

  if (typeof document === 'undefined') return null

  return createPortal(
    <div
      className="spone-modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-label={copy.membership}
    >
      <div
        className="spone-modal-scrim"
        onClick={onClose}
      />

      <div
        className="subscription-paywall-shell subscription-paywall-modal"
        style={{
          maxHeight: 'calc(100dvh - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px) - 24px)',
          overflowY: 'auto',
        }}
      >
        {/* Top Handle Bar (1:1 with Spone AI) */}
        <div className="spone-pro-handle-bar" aria-hidden="true" />

        {/* Close Button */}
        <button
          type="button"
          onClick={onClose}
          className="app-close-button float-island-sheet-close subscription-paywall-close"
          aria-label={text('close')}
        >
          <X size={19} />
        </button>

        {/* Hero Artwork (1:1 with SponeAI Pro Artwork) */}
        <VoxnelaStudioHeroArtwork
          compact
          title={selectedTier === 'studio' ? 'Voxnela Studio' : 'Voxnela Pro'}
        />

        <div className="subscription-paywall-body">
          {/* Plan Tier Toggle (Pro vs Studio) */}
          <div className="subscription-tier-toggle" role="tablist" aria-label={copy.options}>
            <button
              type="button"
              role="tab"
              aria-selected={selectedTier === 'pro'}
              className={selectedTier === 'pro' ? 'is-active' : ''}
              onClick={() => {
                void haptic('selection')
                setSelectedTier('pro')
                setError('')
              }}
            >
              Pro · 75
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={selectedTier === 'studio'}
              className={selectedTier === 'studio' ? 'is-active' : ''}
              onClick={() => {
                void haptic('selection')
                setSelectedTier('studio')
                setError('')
              }}
            >
              Studio · 200
            </button>
          </div>

          {(message || membership.plan === 'free') && (
            <p className="subscription-free-allowance">{message || copy.freeAllowance}</p>
          )}

          {/* Benefits List */}
          <div className="subscription-benefit-list">
            {benefits.map((row) => (
              <div key={row.lead} className="subscription-benefit-list__item">
                <span className="subscription-benefit-list__check">
                  <Check size={12} strokeWidth={3.2} />
                </span>
                <span className="subscription-benefit-list__copy">
                  <strong>{row.lead}</strong> · {row.label}
                </span>
              </div>
            ))}
          </div>

          {/* Period Selector Grid (Aylık & Yıllık) */}
          <div className="subscription-period-grid">
            {/* Monthly Card */}
            <button
              type="button"
              onClick={() => {
                void haptic('selection')
                setSelectedPeriod('monthly')
                setError('')
              }}
              className={`subscription-period-card ${
                selectedPeriod === 'monthly' ? 'is-selected' : ''
              }`}
            >
              <span className="subscription-period-card__radio">
                {selectedPeriod === 'monthly' ? <Check size={12} strokeWidth={3} /> : null}
              </span>
              <strong>{membershipPeriodLabel('monthly', locale)}</strong>
              <span className="subscription-period-card__price">
                {catalogLoading ? copy.loadingPrice : monthlyOffer.priceString}
              </span>
              {isCurrentSubscription(
                membership.plan,
                membership.billingPeriod,
                selectedTier,
                'monthly',
              ) && <small>{copy.currentMembership}</small>}
            </button>

            {/* Yearly Card */}
            <button
              type="button"
              onClick={() => {
                void haptic('selection')
                setSelectedPeriod('yearly')
                setError('')
              }}
              className={`subscription-period-card ${
                selectedPeriod === 'yearly' ? 'is-selected' : ''
              }`}
            >
              <span className="subscription-period-card__badge">{copy.yearlyDiscount}</span>
              <span className="subscription-period-card__radio">
                {selectedPeriod === 'yearly' ? <Check size={12} strokeWidth={3} /> : null}
              </span>
              <strong>{membershipPeriodLabel('yearly', locale)}</strong>
              <span className="subscription-period-card__price">
                {catalogLoading ? copy.loadingPrice : yearlyOffer.priceString}
              </span>
              {isCurrentSubscription(
                membership.plan,
                membership.billingPeriod,
                selectedTier,
                'yearly',
              ) && <small>{copy.currentMembership}</small>}
            </button>
          </div>

          {error && <p className="subscription-paywall-error">{error}</p>}
          {(notice || membershipSyncPending || membershipLoading || workspaceError) && (
            <p className="subscription-paywall-disclosure" role="status">{membershipLoading ? copy.membershipSyncPending : membershipSyncPending || workspaceError ? copy.membershipSyncFailed : notice}</p>
          )}

          {/* Primary CTA Button */}
          <button
            type="button"
            onClick={startSubscription}
            disabled={isActionDisabled}
            className="subscription-paywall-cta flex items-center justify-center gap-2"
          >
            {working ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              <span>{ctaLabel}</span>
            )}
          </button>

          {/* Disclosure text (1:1 with Spone AI) */}
          <p className="subscription-paywall-disclosure">
            {catalogLoading ? copy.loadingStorePrice : currentOffer.priceString} / {formatPaywallMessage(copy.renewDisclosure, { period: selectedPeriod === 'yearly' ? copy.year : copy.month })}
          </p>

          {Capacitor.isNativePlatform() && (
            <button type="button" className="subscription-paywall-restore" disabled={working || restoring} onClick={() => void restorePurchases()}>
              {restoring ? copy.restoringPurchases : copy.restorePurchases}
            </button>
          )}

          {/* Legal Links (1:1 with Spone AI) */}
          <div className="subscription-paywall-legal">
            <Link
              to="/terms"
              onClick={onOpenTerms ?? onClose}
            >
              {copy.legal}
            </Link>
            <span>·</span>
            <Link
              to="/privacy"
              onClick={onOpenPrivacy ?? onClose}
            >
              {text('privacy')}
            </Link>
            <span>·</span>
            <Link
              to="/terms"
              onClick={onOpenTerms ?? onClose}
            >
              {copy.terms}
            </Link>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  )
}
